// core/storage — akses Supabase Storage bersama (foto kuasa, selfie absensi, + list utk Kelola Penyimpanan).
// Bucket privat → akses via signed URL. Path = "<folder>/<uuid>.<ext>" (folder = outlet / emp-scope utk RLS).
import { supabase } from './supabase';

/**
 * Ukuran simpan per JENIS foto. Foto bukan penghuni database — ia yang benar-benar
 * menumpuk: pada 10 cabang × 30 servis/hari, ±117.000 foto/tahun. Disimpan apa adanya
 * (±300 KB) itu 335 GB dalam 10 tahun; dengan ukuran yang pas hanya ±71 GB, yang masih
 * muat di jatah penyimpanan paket berbayar tanpa biaya tambahan.
 *
 * Selfie absensi cuma perlu menjawab "ini orangnya?" → 480px sudah lebih dari cukup.
 * Bukti bayar & KTP HARUS tetap terbaca (itu bukti hukum) → jangan diperkecil berlebihan.
 */
export const FOTO = {
  selfie: { dim: 480, q: 0.7 },      // ±40 KB
  bukti: { dim: 1280, q: 0.75 },     // ±120 KB — angka & nama di struk harus terbaca
  identitas: { dim: 1280, q: 0.8 },  // ±150 KB — NIK & alamat di KTP harus terbaca
} as const;

/** Kompres gambar di klien → SELALU JPEG (maks sisi terpanjang `maxDim`).
 *  LEMPAR error bila input bukan gambar yang bisa didecode → cegah byte non-image
 *  masuk berlabel image/jpeg (bucket allowed_mime_types trust content-type). */
export async function compressImage(file: File | Blob, maxDim = 1600, quality = 0.8): Promise<Blob> {
  let img: ImageBitmap;
  try { img = await createImageBitmap(file); }
  catch { throw new Error('Format foto tidak didukung — gunakan JPEG/PNG.'); }
  let w = img.width, h = img.height; const m = Math.max(w, h);
  if (m > maxDim) { const s = maxDim / m; w = Math.round(w * s); h = Math.round(h * s); }
  const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d'); if (!ctx) throw new Error('Gagal memproses foto.');
  ctx.drawImage(img, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), 'image/jpeg', quality));
  if (!blob) throw new Error('Gagal memproses foto.');
  return blob;
}

/** Upload ke bucket → kembalikan path tersimpan. upsert:false (uuid unik, anti-timpa). */
export async function uploadFile(bucket: string, folder: string, blob: Blob, ext: string, contentType: string): Promise<string> {
  const path = `${folder}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase().storage.from(bucket).upload(path, blob, { contentType, upsert: false });
  if (error) throw new Error(error.message);
  return path;
}

/** Signed URL sementara (default 120 dtk) untuk melihat/mengunduh objek privat. */
export async function signedUrl(bucket: string, path: string, expires = 120): Promise<string> {
  const { data, error } = await supabase().storage.from(bucket).createSignedUrl(path, expires);
  if (error) throw new Error(error.message);
  return data.signedUrl;
}

/** Unduh objek → data URI base64. Untuk EMBED di dokumen cetak/arsip (self-contained,
 *  tak bergantung signed-URL yang kedaluwarsa; aman utk save-as-PDF). */
export async function fetchAsDataUri(bucket: string, path: string): Promise<string> {
  const { data, error } = await supabase().storage.from(bucket).download(path);
  if (error) throw new Error(error.message);
  return await new Promise<string>((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result as string);
    fr.onerror = () => rej(new Error('Gagal membaca foto.'));
    fr.readAsDataURL(data);
  });
}

/** Hapus objek (best-effort di call-site; lempar error bila gagal). */
export async function deleteFile(bucket: string, path: string): Promise<void> {
  const { error } = await supabase().storage.from(bucket).remove([path]);
  if (error) throw new Error(error.message);
}

export interface StoredObject { name: string; path: string; size: number; updatedAt: string | null }
interface RawEntry { name: string; id: string | null; updated_at: string | null; metadata: { size?: number } | null }
const PAGE = 1000;

/** List SEMUA entri (file+folder) di bawah prefix, ter-paginasi (offset) — tak terbatas 1000. */
async function listRaw(bucket: string, prefix: string): Promise<RawEntry[]> {
  const all: RawEntry[] = [];
  for (let offset = 0; ; offset += PAGE) {
    const { data, error } = await supabase().storage.from(bucket).list(prefix, { limit: PAGE, offset, sortBy: { column: 'name', order: 'asc' } });
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as unknown as RawEntry[];
    all.push(...batch);
    if (batch.length < PAGE) break; // halaman terakhir
  }
  return all;
}

const toObj = (o: RawEntry, prefix: string): StoredObject => ({
  name: o.name, path: prefix ? `${prefix}/${o.name}` : o.name, size: o.metadata?.size ?? 0, updatedAt: o.updated_at,
});

/** Daftar objek di bawah `prefix` (1 folder). Entri folder (id null) dibuang — hanya file. Paginasi penuh. */
export async function listFiles(bucket: string, prefix = ''): Promise<StoredObject[]> {
  return (await listRaw(bucket, prefix)).filter((o) => o.id != null).map((o) => toObj(o, prefix));
}

/** SEMUA file dalam bucket (root + tiap subfolder 1 tingkat, mis. "<outlet>/" atau "<emp_id>/").
 *  Path proyek = "<folder>/<uuid>.<ext>" (1 tingkat) → cukup 1 level. Paginasi penuh (tak putus di 1000). */
export async function listAllFiles(bucket: string): Promise<StoredObject[]> {
  const root = await listRaw(bucket, '');
  const rootFiles = root.filter((o) => o.id != null).map((o) => toObj(o, ''));
  const folders = root.filter((o) => o.id == null).map((o) => o.name);
  const sub = await Promise.all(folders.map((f) => listFiles(bucket, f)));
  return [...rootFiles, ...sub.flat()];
}
